import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  bulkCreatePriceAlerts,
  getPriceAlerts,
  cancelPriceAlert,
  getPriceAlertConfig,
  getPriceAlertSummary,
  PriceAlert,
  PriceAlertConfig,
  PriceAlertOperator,
  PriceAlertStatus,
} from '../../composables/fetchAPI';
import { useMarketPrices } from '../../context/MarketPriceContext';

interface PriceAlertToolProps {
  symbol?: string;
}

type LineRole = 'support' | 'resistance';

interface BoardLine {
  key: string; // ใช้ราคาที่ปัดแล้วเป็น key กันเส้นราคาซ้ำ
  price: number;
  role: LineRole;
  tag: string; // S1, S2 / R1, R2 จัดใหม่ทุกครั้งตามราคาปัจจุบัน
  operator: PriceAlertOperator;
  targetPrice: string; // decimal string ตาม contract ของ backend (scale 10000)
}

const SUPPORT_COLOR = '#fde047';
const RESISTANCE_COLOR = '#a78bfa';

const LIVE_STATUSES: PriceAlertStatus[] = ['PENDING_INDEX', 'ACTIVE'];
const ALL_ALERTS_PAGE_SIZE = 50;

const money = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// backend ปัด targetPrice เป็น 4 ตำแหน่ง (priceScale 10000) ต้องส่งเป็น string ไม่ใช่ number
const toTargetString = (price: number) => price.toFixed(4);

// อ่านแค่ "ตัวเลขราคา" ของเส้นจากกระดานเทรด (chartLines_<symbol>) ไม่เชื่อ type/tag ที่เก็บไว้
// เพราะราคาปัจจุบันเปลี่ยนแล้วเส้นแนวรับอาจกลายเป็นแนวต้าน (และกลับกัน) ได้
function readBoardPrices(symbol: string): number[] {
  try {
    const raw = localStorage.getItem(`chartLines_${symbol}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const lines = Array.isArray(parsed) ? parsed : parsed?.lines;
    if (!Array.isArray(lines)) return [];
    const prices = lines.map((l: any) => Number(l?.price)).filter((p: number) => Number.isFinite(p) && p > 0);
    return Array.from(new Set(prices.map(toTargetString))).map(Number);
  } catch {
    return [];
  }
}

// จัดบทบาทเส้นใหม่เทียบกับราคาปัจจุบัน: ต่ำกว่า = แนวรับ (เตือนเมื่อราคาลงมาถึง, LTE),
// สูงกว่า = แนวต้าน (เตือนเมื่อราคาขึ้นไปถึง, GTE) เส้นที่เท่ากับราคาปัจจุบันพอดีตั้งเตือนไม่ได้
// (backend จะตอบ CONDITION_ALREADY_MET) จึงตัดทิ้ง
function classifyLines(prices: number[], currentPrice: number): BoardLine[] {
  const supports = prices.filter((p) => p < currentPrice).sort((a, b) => b - a);
  const resistances = prices.filter((p) => p > currentPrice).sort((a, b) => a - b);
  return [
    ...supports.map((price, idx) => ({
      key: toTargetString(price),
      price,
      role: 'support' as const,
      tag: `S${idx + 1}`,
      operator: 'LTE' as const,
      targetPrice: toTargetString(price),
    })),
    ...resistances.map((price, idx) => ({
      key: toTargetString(price),
      price,
      role: 'resistance' as const,
      tag: `R${idx + 1}`,
      operator: 'GTE' as const,
      targetPrice: toTargetString(price),
    })),
  ];
}

interface AllAlertsPanelProps {
  currentSymbol: string;
  refreshKey: number; // เปลี่ยนค่าเมื่อมีการสร้าง/ยกเลิกจากส่วนเส้นด้านบน ให้ panel โหลดใหม่
  onChanged: () => void; // แจ้งส่วนเส้นด้านบนให้โหลดสถานะใหม่หลังยกเลิกจาก panel นี้
}

// รายการเตือนทั้งหมดที่ backend ถืออยู่จริง (PENDING_INDEX + ACTIVE) ไม่ขึ้นกับเส้นในกราฟ
// เพราะเส้นแนวรับ–แนวต้านเก็บแค่ใน localStorage ถ้าผู้ใช้เลื่อน/ลบเส้นหลังตั้งเตือนไปแล้ว
// การเตือนเดิมยังทำงานอยู่บน backend แต่จะหายจากรายการเส้นด้านบน ต้องมาดู/ยกเลิกที่นี่
const AllAlertsPanel: React.FC<AllAlertsPanelProps> = ({ currentSymbol, refreshKey, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'symbol' | 'all'>(currentSymbol ? 'symbol' : 'all');
  const [items, setItems] = useState<PriceAlert[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [liveCount, setLiveCount] = useState<{ live: number; limit: number } | null>(null);

  const effectiveScope = currentSymbol ? scope : 'all';
  const filterSymbol = effectiveScope === 'symbol' ? currentSymbol : undefined;

  const loadSummary = useCallback(() => {
    getPriceAlertSummary()
      .then((s) => setLiveCount({ live: s.live, limit: s.limit }))
      .catch(() => setLiveCount(null));
  }, []);

  const loadPage = useCallback(
    async (after?: string) => {
      setLoading(true);
      try {
        const res = await getPriceAlerts({
          status: LIVE_STATUSES,
          symbol: filterSymbol,
          limit: ALL_ALERTS_PAGE_SIZE,
          after,
        });
        setItems((prev) => (after ? [...prev, ...(res.items || [])] : res.items || []));
        setNextCursor(res.nextCursor);
      } catch (error: any) {
        toast.error(error?.th_message || error?.message || 'โหลดรายการแจ้งเตือนไม่สำเร็จ');
      } finally {
        setLoading(false);
      }
    },
    [filterSymbol]
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refreshKey]);

  useEffect(() => {
    if (open) loadPage();
  }, [open, loadPage, refreshKey]);

  // เส้นของแต่ละหุ้นอ่านจาก localStorage ของหุ้นนั้นๆ เพื่อบอกว่าการเตือนไหนไม่มีเส้นในกราฟแล้ว
  const boardPricesBySymbol = useMemo(() => {
    const map = new Map<string, Set<string>>();
    items.forEach((a) => {
      if (!map.has(a.symbol)) map.set(a.symbol, new Set(readBoardPrices(a.symbol).map(toTargetString)));
    });
    return map;
  }, [items]);

  const handleCancel = async (id: string) => {
    setCancellingId(id);
    try {
      const result = await cancelPriceAlert(id);
      if (result?.status === 'TRIGGERED') toast('การเตือนนี้ทำงานไปแล้วก่อนยกเลิก');
      else toast.success('ยกเลิกการแจ้งเตือนแล้ว');
    } catch (error: any) {
      toast.error(error?.th_message || error?.message || 'ยกเลิกไม่สำเร็จ');
    } finally {
      setCancellingId(null);
      loadPage();
      loadSummary();
      onChanged();
    }
  };

  return (
    <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--bg-item)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-sm font-semibold"
        style={{ color: 'var(--text-color)' }}
      >
        <span>
          ดูการเตือนทั้งหมด
          {liveCount && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-normal" style={{ background: 'var(--bg-item)' }}>
              ใช้ไป {liveCount.live}/{liveCount.limit}
            </span>
          )}
        </span>
        <span className={`transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--yellow-text-color)' }}>
          ▾
        </span>
      </button>

      {open && (
        <div className="mt-3">
          {currentSymbol && (
            <div className="flex gap-2 mb-3 text-xs">
              {(['symbol', 'all'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className="px-3 py-1 rounded-full font-semibold"
                  style={
                    effectiveScope === s
                      ? { background: 'var(--tab-selected)', color: '#fff' }
                      : { background: 'var(--bg-item)', color: 'var(--text-color)' }
                  }
                >
                  {s === 'symbol' ? `เฉพาะ ${currentSymbol}` : 'ทุกหุ้น'}
                </button>
              ))}
            </div>
          )}

          {items.length === 0 && !loading ? (
            <div className="text-sm py-4 text-center" style={{ color: 'var(--text-color)' }}>
              ยังไม่มีการเตือนที่เปิดอยู่
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {items.map((a) => {
                const isUp = a.operator === 'GTE';
                const target = Number(a.targetPrice);
                const hasLine = boardPricesBySymbol.get(a.symbol)?.has(toTargetString(target));
                return (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2 py-2.5 text-sm"
                    style={{ borderBottom: '1px solid var(--bg-item)' }}
                  >
                    <div className="min-w-0">
                      <div>
                        <span className="font-bold" style={{ color: 'var(--yellow-text-color)' }}>{a.symbol}</span>
                        <span className="ml-2" style={{ color: isUp ? RESISTANCE_COLOR : SUPPORT_COLOR }}>
                          {isUp ? 'ขึ้นถึง ≥' : 'ลงถึง ≤'} {money(target)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs flex-wrap">
                        <span style={{ color: a.status === 'ACTIVE' ? 'var(--green-text-color)' : 'var(--yellow-text-color)' }}>
                          {a.status === 'ACTIVE' ? 'เตือนอยู่' : 'กำลังตั้งค่า'}
                        </span>
                        {!hasLine && (
                          <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--red-text-color)' }}>
                            ไม่มีเส้นนี้ในกราฟแล้ว
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCancel(a.id)}
                      disabled={cancellingId === a.id}
                      className="text-xs font-semibold px-2 py-1 rounded-lg disabled:opacity-50 whitespace-nowrap"
                      style={{ background: 'var(--red-text-color-btn)', color: '#fff' }}
                    >
                      {cancellingId === a.id ? '...' : 'ยกเลิก'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {loading && (
            <div className="text-xs py-2 text-center" style={{ color: 'var(--text-color)' }}>กำลังโหลด...</div>
          )}
          {nextCursor && !loading && (
            <button
              type="button"
              onClick={() => loadPage(nextCursor)}
              className="w-full text-xs py-2 mt-1 underline"
              style={{ color: 'var(--text-color)' }}
            >
              โหลดเพิ่ม
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PriceAlertTool: React.FC<PriceAlertToolProps> = ({ symbol }) => {
  const cleanSymbol = (symbol || '').trim().toUpperCase();
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();
  const currentPrice = cleanSymbol ? currentPrices[cleanSymbol] || 0 : 0;

  const [boardPrices, setBoardPrices] = useState<number[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [liveAlerts, setLiveAlerts] = useState<PriceAlert[]>([]);
  const [config, setConfig] = useState<PriceAlertConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [panelRefreshKey, setPanelRefreshKey] = useState(0);
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cleanSymbol) fetchPricesForSymbols([cleanSymbol]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanSymbol]);

  // โหลดเส้นจาก localStorage ตอนเปลี่ยนหุ้น และตอนกลับมาที่แท็บนี้ (เผื่อไปตีเส้นเพิ่มในกระดานเทรด)
  useEffect(() => {
    if (!cleanSymbol) {
      setBoardPrices([]);
      return;
    }
    const reload = () => setBoardPrices(readBoardPrices(cleanSymbol));
    reload();
    const onStorage = (e: StorageEvent) => {
      if (e.key === `chartLines_${cleanSymbol}`) reload();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', reload);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', reload);
    };
  }, [cleanSymbol]);

  useEffect(() => {
    setSelected(new Set());
  }, [cleanSymbol]);

  useEffect(() => {
    getPriceAlertConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  const loadLiveAlerts = useCallback(async () => {
    if (!cleanSymbol) {
      setLiveAlerts([]);
      return;
    }
    try {
      // list ค่า default คืนแค่ ACTIVE ต้องขอ PENDING_INDEX ด้วย ไม่งั้นเส้นที่เพิ่งตั้งจะหายไป
      const res = await getPriceAlerts({ status: LIVE_STATUSES, symbol: cleanSymbol, limit: 100 });
      setLiveAlerts(res.items || []);
    } catch (error: any) {
      toast.error(error?.th_message || error?.message || 'โหลดรายการแจ้งเตือนไม่สำเร็จ');
    }
  }, [cleanSymbol]);

  useEffect(() => {
    loadLiveAlerts();
  }, [loadLiveAlerts]);

  const lines = useMemo(
    () => (currentPrice > 0 ? classifyLines(boardPrices, currentPrice) : []),
    [boardPrices, currentPrice]
  );

  // จับคู่เส้นกับ alert ที่ตั้งไว้แล้ว (เงื่อนไขซ้ำ = symbol + operator + targetPrice เดียวกัน)
  const alertByLine = useMemo(() => {
    const map = new Map<string, PriceAlert>();
    lines.forEach((l) => {
      const match = liveAlerts.find(
        (a) => a.operator === l.operator && Number(a.targetPrice).toFixed(4) === l.targetPrice
      );
      if (match) map.set(l.key, match);
    });
    return map;
  }, [lines, liveAlerts]);

  const selectableLines = lines.filter((l) => !alertByLine.has(l.key));

  // ราคาขยับจนเส้นสลับฝั่ง/หายไป ให้ตัดตัวที่เลือกไว้แต่ไม่มีแล้วออก
  useEffect(() => {
    setSelected((prev) => {
      const valid = new Set(selectableLines.map((l) => l.key));
      const next = new Set(Array.from(prev).filter((k) => valid.has(k)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableLines.map((l) => l.key).join(',')]);

  const selectedCount = selectableLines.filter((l) => selected.has(l.key)).length;
  const allSelected = selectableLines.length > 0 && selectedCount === selectableLines.length;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
  }, [selectedCount, allSelected]);

  const toggleLine = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableLines.map((l) => l.key)));
  };

  const accepting = config ? config.enabled && config.accepting : true;

  const handleCreate = async () => {
    const targets = selectableLines.filter((l) => selected.has(l.key));
    if (!targets.length) return toast.error('กรุณาเลือกเส้นที่ต้องการตั้งเตือน');
    if (config && targets.length > config.bulkMax) {
      return toast.error(`เลือกได้สูงสุด ${config.bulkMax} เส้นต่อครั้ง`);
    }

    setCreating(true);
    try {
      // bulk เป็น all-or-nothing ถ้ามีเส้นไหนผิด (ราคาถึงแล้ว/ซ้ำ/เกินโควตา) จะไม่มีเส้นไหนถูกสร้าง
      await bulkCreatePriceAlerts(
        targets.map((l) => ({ symbol: cleanSymbol, targetPrice: l.targetPrice, operator: l.operator }))
      );
      toast.success(`เปิดการเตือน ${targets.length} เส้นแล้ว`);
      setSelected(new Set());
    } catch (error: any) {
      toast.error(error?.th_message || error?.message || 'ตั้งเตือนราคาไม่สำเร็จ');
    } finally {
      setCreating(false);
      loadLiveAlerts();
      setPanelRefreshKey((k) => k + 1);
    }
  };

  const handleCancel = async (id: string) => {
    setCancellingId(id);
    try {
      const result = await cancelPriceAlert(id);
      // ถ้า trigger ชนะ race จะได้ TRIGGERED กลับมาแทน ต้องบอกตามจริง
      if (result?.status === 'TRIGGERED') toast('เส้นนี้แจ้งเตือนไปแล้วก่อนยกเลิก');
      else toast.success('ยกเลิกการแจ้งเตือนแล้ว');
    } catch (error: any) {
      toast.error(error?.th_message || error?.message || 'ยกเลิกไม่สำเร็จ');
    } finally {
      setCancellingId(null);
      loadLiveAlerts();
      setPanelRefreshKey((k) => k + 1);
    }
  };

  const allAlertsPanel = (
    <AllAlertsPanel currentSymbol={cleanSymbol} refreshKey={panelRefreshKey} onChanged={loadLiveAlerts} />
  );

  if (!cleanSymbol) {
    return (
      <div className="p-4">
        <div className="text-sm text-center py-2" style={{ color: 'var(--text-color)' }}>
          โปรดเลือกหุ้นทางซ้ายก่อน
        </div>
        {allAlertsPanel}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <div className="text-2xl font-bold" style={{ color: 'var(--yellow-text-color)' }}>
            {cleanSymbol}
          </div>
          <div className="text-sm" style={{ color: 'var(--text-color)' }}>
            ราคาอ้างอิง {currentPrice > 0 ? money(currentPrice) : 'กำลังโหลด...'}
          </div>
        </div>
        {/* <span className="text-xs px-2 py-1 rounded-md" style={{ background: 'var(--bg-item)', color: 'var(--text-color)' }}>
          ยังไม่ส่งจริง
        </span> */}
      </div>

      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
          เลือกเส้นจากกระดานเทรดของฉัน
        </span>
        {selectableLines.length > 0 && (
          <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-color)' }}>
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="w-4 h-4 cursor-pointer"
              style={{ accentColor: SUPPORT_COLOR }}
            />
            เลือกทั้งหมด
          </label>
        )}
      </div>

      {currentPrice <= 0 ? (
        <div className="text-sm py-6 text-center" style={{ color: 'var(--text-color)' }}>
          กำลังโหลดราคาปัจจุบัน...
        </div>
      ) : lines.length === 0 ? (
        <div className="text-sm py-6 text-center" style={{ color: 'var(--text-color)' }}>
          ยังไม่มีเส้นแนวรับ–แนวต้านของ {cleanSymbol} ในกระดานเทรด
        </div>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          {lines.map((l) => {
            const color = l.role === 'support' ? SUPPORT_COLOR : RESISTANCE_COLOR;
            const diffPct = (Math.abs(l.price - currentPrice) / currentPrice) * 100;
            const existing = alertByLine.get(l.key);
            return (
              <div
                key={l.key}
                className="flex items-center justify-between gap-2 py-3 text-sm"
                style={{ borderBottom: '1px solid var(--bg-item)' }}
              >
                <label className={`flex items-center gap-3 ${existing ? '' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={existing ? true : selected.has(l.key)}
                    disabled={!!existing}
                    onChange={() => toggleLine(l.key)}
                    className="w-4 h-4 cursor-pointer disabled:cursor-default disabled:opacity-50"
                    style={{ accentColor: SUPPORT_COLOR }}
                  />
                  <span style={{ color }}>
                    {l.tag} · {l.role === 'support' ? 'แนวรับ' : 'แนวต้าน'} {money(l.price)}
                  </span>
                </label>
                <span className="flex items-center gap-2 whitespace-nowrap" style={{ color: 'var(--text-color)' }}>
                  {existing && (
                    <>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--bg-item)', color: existing.status === 'ACTIVE' ? 'var(--green-text-color)' : 'var(--yellow-text-color)' }}
                      >
                        {existing.status === 'ACTIVE' ? 'เตือนอยู่' : 'กำลังตั้งค่า'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCancel(existing.id)}
                        disabled={cancellingId === existing.id}
                        className="text-xs underline disabled:opacity-50"
                        style={{ color: 'var(--red-text-color)' }}
                      >
                        {cancellingId === existing.id ? '...' : 'ยกเลิก'}
                      </button>
                    </>
                  )}
                  {l.role === 'support' ? 'ต่ำกว่า' : 'สูงกว่า'} {diffPct.toFixed(2)}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={handleCreate}
        disabled={creating || selectedCount === 0 || !accepting}
        className="w-full rounded-lg py-3 mt-4 font-bold text-sm disabled:opacity-50"
        style={{ background: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
      >
        {!accepting
          ? 'ระบบแจ้งเตือนราคาปิดให้บริการชั่วคราว'
          : creating
            ? 'กำลังเปิดการเตือน...'
            : `เปิดการเตือน${selectedCount ? ` (${selectedCount} เส้น)` : ''}`}
      </button>

      {allAlertsPanel}
    </div>
  );
};

export default PriceAlertTool;
